/**
 * src/components/pwa/InstallPrompt.tsx
 * Per D-20, Plan 03-05 — engagement-gated PWA install prompt slide-in card.
 *
 * Estrutura herdada do antigo PushSlidePrompt (removido em 2026-10-20):
 *   - <aside role="dialog" aria-modal="false" data-pwa-prompt>
 *   - Position: mobile above StickyBar + desktop bottom-right
 *   - z-50 (orchestrator guarantees mutex with push/exitIntent)
 *
 * iOS Safari fallback (Pitfall E): shows manual "Share → Add to Home Screen" instructions
 * instead of native beforeinstallprompt (which never fires on iOS).
 *
 * Microcopy frozen per plan spec. ActionButton preserved (Phase 2 freeze).
 */

import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import { ActionButton } from "@/components/ui/action-button";

export function InstallPrompt() {
  const { canShow, iosFallback, accept, dismiss } = useInstallPrompt();

  if (!canShow) return null;

  const title = "📲 Instalar Macaco das Promos?";
  const body = iosFallback
    ? "Toque em Compartilhar → Adicionar à Tela de Início. Promos relâmpago direto no ícone."
    : "Acesso instantâneo, sem precisar abrir o navegador. Notificações relâmpago direto no ícone.";

  return (
    <aside
      role="dialog"
      aria-modal="false"
      aria-label="Convite para instalar o app Macaco das Promos"
      data-pwa-prompt
      className={[
        "fixed z-50",
        "bottom-[calc(80px+env(safe-area-inset-bottom)+8px)] left-3 right-3",
        "md:bottom-4 md:right-4 md:left-auto w-auto md:w-[360px] max-w-[400px]",
        "rounded-2xl bg-card border border-card-border/15 shadow-hover p-4",
        "transition-transform duration-300 ease-out motion-reduce:transition-none",
        "translate-y-0 opacity-100",
      ].join(" ")}
    >
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{body}</p>
      <div className="mt-3 flex gap-2">
        {!iosFallback && (
          <ActionButton
            size="sm"
            onClick={accept}
            aria-label="Confirmar instalação do app"
          >
            Instalar
          </ActionButton>
        )}
        <button
          onClick={dismiss}
          className="text-sm text-muted-foreground underline underline-offset-2 min-h-[44px] px-2"
          aria-label="Fechar convite de instalação"
        >
          Agora não
        </button>
      </div>
    </aside>
  );
}
